import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Fa3BackfillMode,
  InvoiceImportHistoryDto,
  Fa3RetryApproveResultDto,
  Fa3SelectiveRecomputeResultDto,
  InvoiceImportStatusDto,
} from '../Types/invoiceImport.types';
import {
  useGetImportHistoryQuery,
  useRecomputeFa3ForExistingExpenseInvoicesMutation,
  useRetryApproveImportJobsMutation,
  useLazyGetImportStatusQuery,
} from '../Api/invoiceImportApi';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import {
  formatFa3ReasonCodeLabel,
  formatFa3RelationStatusLabel,
  formatFa3WarningLabel,
  formatImportJobStatusLabel,
  formatRetryApproveActionLabel,
  formatRetryApproveMessageLabel,
  getFa3ReasonCodeColor,
} from '../Utils/fa3BackfillLabels';

interface Fa3BackfillOperationsPanelProps {
  autoFocus?: boolean;
  canExecuteOperations?: boolean;
}

interface ParsedPositiveIdsResult {
  ids: number[];
  hasInvalidToken: boolean;
}

const RECENT_IMPORT_JOBS_LIMIT = 20;

const parsePositiveIds = (rawValue: string): ParsedPositiveIdsResult => {
  const tokens = rawValue
    .split(/[\s,;\n]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

  let hasInvalidToken = false;
  const ids: number[] = [];

  for (const token of tokens) {
    if (!/^[1-9]\d*$/.test(token)) {
      hasInvalidToken = true;
      continue;
    }

    ids.push(Number(token));
  }

  return {
    ids: Array.from(new Set(ids)),
    hasInvalidToken,
  };
};

const Fa3BackfillOperationsPanel: React.FC<Fa3BackfillOperationsPanelProps> = ({
  autoFocus = false,
  canExecuteOperations = false,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);

  const [recomputeMode, setRecomputeMode] = useState<Fa3BackfillMode>(Fa3BackfillMode.DryRun);
  const [recomputeInvoiceIds, setRecomputeInvoiceIds] = useState('');
  const [recomputeDateFrom, setRecomputeDateFrom] = useState('');
  const [recomputeDateTo, setRecomputeDateTo] = useState('');
  const [includeManualFixed, setIncludeManualFixed] = useState(false);
  const [manualFixedConfirmation, setManualFixedConfirmation] = useState(false);
  const [recomputeExecuteConfirmation, setRecomputeExecuteConfirmation] = useState(false);
  const [recomputeCorrelationId, setRecomputeCorrelationId] = useState('');

  const [retryApproveMode, setRetryApproveMode] = useState<Fa3BackfillMode>(Fa3BackfillMode.DryRun);
  const [retryApproveExecuteConfirmation, setRetryApproveExecuteConfirmation] = useState(false);
  const [retryApproveImportJobIds, setRetryApproveImportJobIds] = useState('');
  const [retryApproveCorrelationId, setRetryApproveCorrelationId] = useState('');

  const [recomputeValidationError, setRecomputeValidationError] = useState<string | null>(null);
  const [retryApproveValidationError, setRetryApproveValidationError] = useState<string | null>(null);
  const [recomputeError, setRecomputeError] = useState<string | null>(null);
  const [retryApproveError, setRetryApproveError] = useState<string | null>(null);
  const [statusRefreshError, setStatusRefreshError] = useState<string | null>(null);
  const [recomputeResult, setRecomputeResult] = useState<Fa3SelectiveRecomputeResultDto | null>(null);
  const [retryApproveResult, setRetryApproveResult] = useState<Fa3RetryApproveResultDto | null>(null);
  const [selectedImportJobsStatus, setSelectedImportJobsStatus] = useState<InvoiceImportStatusDto[]>([]);
  const [selectedRecentImportJobIds, setSelectedRecentImportJobIds] = useState<number[]>([]);

  const [runRecompute, { isLoading: isRecomputeRunning }] =
    useRecomputeFa3ForExistingExpenseInvoicesMutation();
  const [runRetryApprove, { isLoading: isRetryApproveRunning }] = useRetryApproveImportJobsMutation();
  const [triggerGetImportStatus, { isFetching: isStatusRefreshing }] = useLazyGetImportStatusQuery();
  const {
    data: recentImportHistory,
    isLoading: isRecentImportHistoryLoading,
    isError: isRecentImportHistoryError,
    error: recentImportHistoryError,
  } = useGetImportHistoryQuery({
    pageNumber: 1,
    pageSize: RECENT_IMPORT_JOBS_LIMIT,
  });

  useEffect(() => {
    if (autoFocus) {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!canExecuteOperations) {
      if (recomputeMode === Fa3BackfillMode.Execute) {
        setRecomputeMode(Fa3BackfillMode.DryRun);
      }
      if (retryApproveMode === Fa3BackfillMode.Execute) {
        setRetryApproveMode(Fa3BackfillMode.DryRun);
      }
      setRecomputeExecuteConfirmation(false);
      setRetryApproveExecuteConfirmation(false);
    }
  }, [canExecuteOperations, recomputeMode, retryApproveMode]);

  useEffect(() => {
    const availableIds = new Set((recentImportHistory?.items ?? []).map((item) => item.importJobId));
    setSelectedRecentImportJobIds((current) => current.filter((id) => availableIds.has(id)));
  }, [recentImportHistory]);

  const handleRecomputeSubmit = async () => {
    setRecomputeValidationError(null);
    setRecomputeError(null);
    setRecomputeResult(null);

    const { ids: invoiceIds, hasInvalidToken } = parsePositiveIds(recomputeInvoiceIds);
    const hasDateFrom = recomputeDateFrom.trim().length > 0;
    const hasDateTo = recomputeDateTo.trim().length > 0;
    const hasDateSelector = hasDateFrom && hasDateTo;

    if (hasInvalidToken) {
      setRecomputeValidationError(
        'ID faktur muszą zawierać wyłącznie dodatnie liczby całkowite (format dziesiętny).'
      );
      return;
    }

    if (!invoiceIds.length && !hasDateFrom && !hasDateTo) {
      setRecomputeValidationError('Podaj selektor: ID faktur lub pełny zakres dat (od/do).');
      return;
    }

    if (hasDateFrom !== hasDateTo) {
      setRecomputeValidationError('Data od i data do muszą być podane razem.');
      return;
    }

    if (hasDateSelector && recomputeDateFrom > recomputeDateTo) {
      setRecomputeValidationError('Data od nie może być późniejsza niż data do.');
      return;
    }

    if (invoiceIds.length > 0 && hasDateSelector) {
      setRecomputeValidationError('Wybierz jeden selektor: ID faktur albo zakres dat (od/do).');
      return;
    }

    if (recomputeMode === Fa3BackfillMode.Execute && !recomputeExecuteConfirmation) {
      setRecomputeValidationError('W trybie execute wymagane jest jawne potwierdzenie operacji.');
      return;
    }

    if (recomputeMode === Fa3BackfillMode.Execute && !canExecuteOperations) {
      setRecomputeValidationError('Brak uprawnień do uruchamiania operacji w trybie execute.');
      return;
    }

    if (
      recomputeMode === Fa3BackfillMode.Execute &&
      includeManualFixed &&
      !manualFixedConfirmation
    ) {
      setRecomputeValidationError(
        'Dla includeManualFixed=true wymagane jest jawne potwierdzenie nadpisania.'
      );
      return;
    }

    try {
      const response = await runRecompute({
        mode: recomputeMode,
        includeManualFixed,
        correlationId: recomputeCorrelationId.trim() || undefined,
        ...(invoiceIds.length ? { invoiceIds } : {}),
        ...(hasDateSelector
          ? { dateFrom: recomputeDateFrom, dateTo: recomputeDateTo }
          : {}),
      }).unwrap();

      setRecomputeResult(response);
    } catch (error) {
      setRecomputeError(extractApiErrorMessage(error, 'Nie udało się uruchomić recompute FA(3).'));
    }
  };

  const handleRetryApproveSubmit = async () => {
    setRetryApproveValidationError(null);
    setRetryApproveError(null);
    setRetryApproveResult(null);

    const { ids: importJobIds, hasInvalidToken } = parsePositiveIds(retryApproveImportJobIds);

    if (hasInvalidToken || !importJobIds.length) {
      setRetryApproveValidationError(
        'ID import jobów muszą zawierać co najmniej jedną dodatnią liczbę całkowitą (format dziesiętny).'
      );
      return;
    }

    if (retryApproveMode === Fa3BackfillMode.Execute && !retryApproveExecuteConfirmation) {
      setRetryApproveValidationError('W trybie execute wymagane jest jawne potwierdzenie operacji.');
      return;
    }

    if (retryApproveMode === Fa3BackfillMode.Execute && !canExecuteOperations) {
      setRetryApproveValidationError('Brak uprawnień do uruchamiania operacji w trybie execute.');
      return;
    }

    try {
      const response = await runRetryApprove({
        mode: retryApproveMode,
        importJobIds,
        correlationId: retryApproveCorrelationId.trim() || undefined,
      }).unwrap();

      setRetryApproveResult(response);
    } catch (error) {
      setRetryApproveError(
        extractApiErrorMessage(error, 'Nie udało się uruchomić split-flow retry/approve.')
      );
    }
  };

  const handleRefreshSelectedImportJobsStatus = async () => {
    setRetryApproveValidationError(null);
    setStatusRefreshError(null);
    setSelectedImportJobsStatus([]);

    const { ids: importJobIds, hasInvalidToken } = parsePositiveIds(retryApproveImportJobIds);

    if (hasInvalidToken || !importJobIds.length) {
      setRetryApproveValidationError(
        'Aby pobrać status, podaj ID import jobów jako dodatnie liczby całkowite (format dziesiętny).'
      );
      return;
    }

    try {
      const statusResults = await Promise.allSettled(
        importJobIds.map((importJobId) => triggerGetImportStatus(importJobId).unwrap())
      );

      const successfulRows: InvoiceImportStatusDto[] = [];
      const failedImportJobIds: number[] = [];
      const rejectionReasons: unknown[] = [];

      statusResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          successfulRows.push(result.value);
          return;
        }

        failedImportJobIds.push(importJobIds[index]);
        rejectionReasons.push(result.reason);
      });

      setSelectedImportJobsStatus(successfulRows);

      if (failedImportJobIds.length) {
        const firstFailureReason = rejectionReasons[0];
        const failureMessage = extractApiErrorMessage(
          firstFailureReason,
          'Nie udało się pobrać statusu części import jobów.'
        );
        setStatusRefreshError(
          `${failureMessage} Niepowodzenie dla IDs: ${failedImportJobIds.join(', ')}.`
        );
      }
    } catch (error) {
      setStatusRefreshError(
        extractApiErrorMessage(error, 'Nie udało się pobrać statusu wybranych import jobów.')
      );
    }
  };

  const handleToggleRecentImportJob = (importJobId: number, checked: boolean) => {
    setSelectedRecentImportJobIds((current) => {
      if (checked) {
        return current.includes(importJobId) ? current : [...current, importJobId];
      }

      return current.filter((id) => id !== importJobId);
    });
  };

  const handleSelectAllRecentImportJobs = () => {
    setSelectedRecentImportJobIds((recentImportHistory?.items ?? []).map((item) => item.importJobId));
  };

  const handleClearRecentImportJobSelection = () => {
    setSelectedRecentImportJobIds([]);
  };

  const handleInsertSelectedImportJobIds = () => {
    if (!selectedRecentImportJobIds.length) {
      return;
    }

    const existingIds = parsePositiveIds(retryApproveImportJobIds).ids;
    const mergedIds = Array.from(new Set([...existingIds, ...selectedRecentImportJobIds]));
    setRetryApproveImportJobIds(mergedIds.join(', '));
    setRetryApproveValidationError(null);
  };

  const recentImportJobs: InvoiceImportHistoryDto[] = recentImportHistory?.items ?? [];

  return (
    <Card ref={panelRef} variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Operacje FA(3): selektywne uzupełnianie
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Uruchamiaj symulację lub wykonanie dla przeliczenia istniejących faktur oraz ponów/zatwierdź
          import joby split-flow. Operacje działają wyłącznie dla jawnych selektorów.
        </Typography>
        {!canExecuteOperations ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Dostęp ograniczony do dry-run. Tryb execute wymaga uprawnień administratora.
          </Alert>
        ) : null}
        <Divider sx={{ mb: 2 }} />

        {(isRecomputeRunning || isRetryApproveRunning) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Trwa wykonywanie operacji FA(3)...
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Typography variant="subtitle1" gutterBottom>
              Przeliczenie FA(3) dla faktur kosztowych
            </Typography>

            <Stack spacing={2}>
              <TextField
                select
                label="Tryb"
                value={recomputeMode}
                onChange={(event) => {
                  const mode = Number(event.target.value) as Fa3BackfillMode;
                  setRecomputeMode(mode);
                  if (mode !== Fa3BackfillMode.Execute) {
                    setRecomputeExecuteConfirmation(false);
                  }
                }}
                size="small"
              >
                <MenuItem value={Fa3BackfillMode.DryRun}>Symulacja (dry-run)</MenuItem>
                {canExecuteOperations ? (
                  <MenuItem value={Fa3BackfillMode.Execute}>Wykonanie (execute)</MenuItem>
                ) : null}
              </TextField>

              {recomputeMode === Fa3BackfillMode.Execute ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={recomputeExecuteConfirmation}
                      onChange={(event) => setRecomputeExecuteConfirmation(event.target.checked)}
                    />
                  }
                  label="Potwierdzam wykonanie (operacja zapisze zmiany)"
                />
              ) : null}

              <TextField
                label="ID faktur (opcjonalnie)"
                value={recomputeInvoiceIds}
                onChange={(event) => setRecomputeInvoiceIds(event.target.value)}
                size="small"
                placeholder="np. 101, 102, 103"
                helperText="Lista ID oddzielona przecinkami/spacjami."
              />

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="date"
                      label="Data od"
                      value={recomputeDateFrom}
                      onChange={(event) => setRecomputeDateFrom(event.target.value)}
                    size="small"
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="date"
                      label="Data do"
                      value={recomputeDateTo}
                      onChange={(event) => setRecomputeDateTo(event.target.value)}
                    size="small"
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
              </Grid>

              <FormControlLabel
                control={
                  <Switch
                    checked={includeManualFixed}
                    onChange={(event) => {
                      setIncludeManualFixed(event.target.checked);
                      if (!event.target.checked) {
                        setManualFixedConfirmation(false);
                      }
                    }}
                  />
                }
                label="Uwzględnij ręcznie naprawione (includeManualFixed)"
              />

              {includeManualFixed ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={manualFixedConfirmation}
                      onChange={(event) => setManualFixedConfirmation(event.target.checked)}
                    />
                  }
                    label="Potwierdzam nadpisanie includeManualFixed=true dla wykonania"
                  />
                ) : null}

              <TextField
                label="Identyfikator korelacji (opcjonalnie)"
                value={recomputeCorrelationId}
                onChange={(event) => setRecomputeCorrelationId(event.target.value)}
                size="small"
              />

              {recomputeValidationError ? <Alert severity="warning">{recomputeValidationError}</Alert> : null}
              {recomputeError ? <Alert severity="error">{recomputeError}</Alert> : null}

              <Button
                variant="contained"
                onClick={handleRecomputeSubmit}
                disabled={isRecomputeRunning}
              >
                  {isRecomputeRunning ? 'Wykonywanie...' : 'Uruchom przeliczenie'}
                </Button>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Typography variant="subtitle1" gutterBottom>
              Ponów/zatwierdź import joby split-flow
            </Typography>

            <Stack spacing={2}>
              <TextField
                select
                label="Tryb"
                value={retryApproveMode}
                onChange={(event) => {
                  const mode = Number(event.target.value) as Fa3BackfillMode;
                  setRetryApproveMode(mode);
                  if (mode !== Fa3BackfillMode.Execute) {
                    setRetryApproveExecuteConfirmation(false);
                  }
                }}
                size="small"
              >
                <MenuItem value={Fa3BackfillMode.DryRun}>Symulacja (dry-run)</MenuItem>
                {canExecuteOperations ? (
                  <MenuItem value={Fa3BackfillMode.Execute}>Wykonanie (execute)</MenuItem>
                ) : null}
              </TextField>

              {retryApproveMode === Fa3BackfillMode.Execute ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={retryApproveExecuteConfirmation}
                      onChange={(event) => setRetryApproveExecuteConfirmation(event.target.checked)}
                    />
                  }
                  label="Potwierdzam wykonanie retry/approve (operacja zapisze zmiany)"
                />
              ) : null}

              <TextField
                label="ID import jobów (wymagane)"
                value={retryApproveImportJobIds}
                onChange={(event) => setRetryApproveImportJobIds(event.target.value)}
                size="small"
                placeholder="np. 201, 202"
                helperText="Znajdziesz je w Historii importu, w kolumnie „ID import jobu”."
              />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Szybki wybór ostatnich import jobów
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleSelectAllRecentImportJobs}
                    disabled={!recentImportJobs.length}
                  >
                    Zaznacz wszystko
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleClearRecentImportJobSelection}
                    disabled={!selectedRecentImportJobIds.length}
                  >
                    Wyczyść zaznaczenie
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleInsertSelectedImportJobIds}
                    disabled={!selectedRecentImportJobIds.length}
                  >
                    Wstaw zaznaczone ID
                  </Button>
                </Stack>

                {isRecentImportHistoryError ? (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {extractApiErrorMessage(
                      recentImportHistoryError,
                      'Nie udało się pobrać listy ostatnich import jobów.'
                    )}
                  </Alert>
                ) : null}

                {isRecentImportHistoryLoading ? (
                  <Typography variant="caption" color="text.secondary">
                    Ładowanie ostatnich import jobów...
                  </Typography>
                ) : recentImportJobs.length ? (
                  <TableContainer sx={{ maxHeight: 220 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell>ID import jobu</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Plik</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentImportJobs.map((item) => (
                          <TableRow key={item.importJobId} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={selectedRecentImportJobIds.includes(item.importJobId)}
                                onChange={(event) =>
                                  handleToggleRecentImportJob(item.importJobId, event.target.checked)
                                }
                                inputProps={{ 'aria-label': `Wybierz import job ${item.importJobId}` }}
                              />
                            </TableCell>
                            <TableCell>{item.importJobId}</TableCell>
                            <TableCell>{item.statusLabel}</TableCell>
                            <TableCell>{item.fileName}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Brak ostatnich import jobów do wyboru.
                  </Typography>
                )}
              </Box>

              <TextField
                label="Identyfikator korelacji (opcjonalnie)"
                value={retryApproveCorrelationId}
                onChange={(event) => setRetryApproveCorrelationId(event.target.value)}
                size="small"
              />

              {retryApproveValidationError ? (
                <Alert severity="warning">{retryApproveValidationError}</Alert>
              ) : null}
              {retryApproveError ? <Alert severity="error">{retryApproveError}</Alert> : null}

              <Button
                variant="outlined"
                onClick={handleRetryApproveSubmit}
                disabled={isRetryApproveRunning}
              >
                  {isRetryApproveRunning ? 'Wykonywanie...' : 'Uruchom ponów/zatwierdź'}
                </Button>

              <Button
                variant="text"
                onClick={handleRefreshSelectedImportJobsStatus}
                disabled={isStatusRefreshing}
              >
                {isStatusRefreshing
                  ? 'Odświeżanie statusów...'
                  : 'Odśwież status wybranych import jobów'}
              </Button>

              {statusRefreshError ? <Alert severity="error">{statusRefreshError}</Alert> : null}
            </Stack>
          </Grid>
        </Grid>

        {recomputeResult ? (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Raport recompute FA(3)
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <Chip color="success" label={`Przetworzone: ${recomputeResult.processedCount}`} size="small" />
              <Chip color="warning" label={`Pominięte: ${recomputeResult.skippedCount}`} size="small" />
              <Chip color="error" label={`Błędne: ${recomputeResult.failedCount}`} size="small" />
            </Stack>

            {recomputeResult.warnings.length ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Ostrzeżenia:</strong>{' '}
                {recomputeResult.warnings.map((warning) => formatFa3WarningLabel(warning)).join(' | ')}
              </Alert>
            ) : null}

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID faktury</TableCell>
                    <TableCell>Źródłowy numer KSeF</TableCell>
                    <TableCell>Faktura referencyjna</TableCell>
                    <TableCell>Referencyjny numer KSeF</TableCell>
                    <TableCell>Status relacji</TableCell>
                    <TableCell>Kod przyczyny</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recomputeResult.items.map((item, index) => (
                    <TableRow key={`${item.invoiceId}-${index}`}>
                      <TableCell>{item.invoiceId}</TableCell>
                      <TableCell>{item.sourceKsefNumber || '—'}</TableCell>
                      <TableCell>{item.referenceInvoiceNumber || '—'}</TableCell>
                      <TableCell>{item.referenceKsefNumber || '—'}</TableCell>
                      <TableCell>{formatFa3RelationStatusLabel(item.relationStatus)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={getFa3ReasonCodeColor(item.reasonCode)}
                          label={formatFa3ReasonCodeLabel(item.reasonCode)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : null}

        {selectedImportJobsStatus.length ? (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Status import jobów (/status)
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID import jobu</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Etykieta statusu</TableCell>
                    <TableCell>Czy wymaga weryfikacji</TableCell>
                    <TableCell>Ostrzeżenia</TableCell>
                    <TableCell>Czas [ms]</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedImportJobsStatus.map((item) => (
                    <TableRow key={item.importJobId}>
                      <TableCell>{item.importJobId}</TableCell>
                      <TableCell>{item.status}</TableCell>
                      <TableCell>{item.statusLabel}</TableCell>
                      <TableCell>{item.requiresReview ? 'Tak' : 'Nie'}</TableCell>
                      <TableCell>{item.warnings.length}</TableCell>
                      <TableCell>{item.processingDurationMs ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : null}

        {retryApproveResult ? (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Raport ponów/zatwierdź
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <Chip color="success" label={`Przetworzone: ${retryApproveResult.processedCount}`} size="small" />
              <Chip color="warning" label={`Pominięte: ${retryApproveResult.skippedCount}`} size="small" />
              <Chip color="error" label={`Błędne: ${retryApproveResult.failedCount}`} size="small" />
            </Stack>

            {retryApproveResult.warnings.length ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Ostrzeżenia:</strong>{' '}
                {retryApproveResult.warnings.map((warning) => formatFa3WarningLabel(warning)).join(' | ')}
              </Alert>
            ) : null}

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID import jobu</TableCell>
                    <TableCell>Poprzedni status</TableCell>
                    <TableCell>Akcja</TableCell>
                    <TableCell>Kod przyczyny</TableCell>
                    <TableCell>Komunikat</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {retryApproveResult.items.map((item, index) => (
                    <TableRow key={`${item.importJobId}-${index}`}>
                      <TableCell>{item.importJobId}</TableCell>
                      <TableCell>{formatImportJobStatusLabel(item.previousStatus)}</TableCell>
                      <TableCell>{formatRetryApproveActionLabel(item.action)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={getFa3ReasonCodeColor(item.reasonCode)}
                          label={formatFa3ReasonCodeLabel(item.reasonCode)}
                        />
                      </TableCell>
                      <TableCell>{formatRetryApproveMessageLabel(item.message)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default Fa3BackfillOperationsPanel;
